import React, { useState, useCallback, useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { FaBars, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Link, Outlet, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';

import './css/Menus.css';
import { useAuth } from '@/src/app/context/useAuth';
import { SimpleButton } from './SimpleForm';
import { UserIcon, LogoutIcon } from './SVGIcons';
import { apiRequest } from '@/src/services';
import { sanitizeGradeShortCode, MenuItems, type MenuItem } from '@/src/utils';
import logo from '@/src/assets/images/Paradigm-Math-logo-dark-200x102.png';
import { useMediaQuery } from '@/src/hooks';

const Menus: React.FC<{ isExamView: boolean }> = ({ isExamView = false }) => {
    const isMobile = useMediaQuery('(max-width: 920px)');
    const defaultOpen = isMobile ? false : localStorage.getItem('pm-menu-open') === 'true';

    const [menuOpen, setMenuOpen] = useState(defaultOpen);
    const { user, logout } = useAuth();
    const grade = user?.alternateGradeColorShortCode
        ? sanitizeGradeShortCode(user?.alternateGradeColorShortCode)
        : sanitizeGradeShortCode(user?.gradeShortCode);
    const isImpersonating = !!Cookies.get('isImpersonating');
    const location = useLocation();

    const [activeSection, setActiveSection] = useState<string | null>(null);

    const setMenuOpenAndSave = (isOpen: boolean) => {
        setMenuOpen(isOpen);
        localStorage.setItem('pm-menu-open', isOpen ? 'true' : 'false');
    };

    const gradeColorButtonSx = {
        backgroundColor: `var(--pm-g${grade}-light)`,
        color: 'var(--pm-primary)',
        ml: 1,
        '&:hover': {
            color: 'var(--pm-primary)',
            backgroundColor: 'var(--pm-accent)',
        },
    };

    const examViewPathRegex = /^\/studentExamView\/attempt\/\d+\/q\/\d+$/i;
    const isExamViewQuestionPage = examViewPathRegex.test(location.pathname);
    const containerBg = isExamViewQuestionPage ? 'var(--pm-secondary-light)' : 'var(--pm-bg)';

    useEffect(() => {
        if (isMobile) {
            document.body.style.overflow = menuOpen ? 'hidden' : '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [menuOpen, isMobile]);

    const isActive = (href: string) => {
        return location.pathname.toLowerCase().startsWith(href.toLowerCase());
    };

    const handleStopImpersonation = useCallback(async () => {
        try {
            await apiRequest('post', 'dbo/users/endImpersonation');
            Cookies.remove('ImpersonationToken');
            Cookies.remove('isImpersonating');
            logout(false);
            window.location.href = '/user/dashboard';
        } catch (err) {
            console.error('Failed to end impersonation', err);
        }
    }, [logout]);

    const renderNotification = useCallback(
        (key: string) => {
            if (key === 'Handscore') {
                const count = user?.handscoreQuestionCount || 0;
                if (count > 0) {
                    return (
                        <span className="pm-badge" aria-label={`${count} new`}>
                            {count}
                        </span>
                    );
                }
            }
            return null;
        },
        [user?.handscoreQuestionCount]
    );

    const menuBySection = (MenuItems as MenuItem[]).reduce(
        (acc, item) => {
            if (!item.roles.includes(user?.role ?? '')) return acc;
            if (!acc[item.section]) acc[item.section] = [];
            acc[item.section].push(item);
            return acc;
        },
        {} as Record<string, typeof MenuItems>
    );

    useEffect(() => {
        const currentPath = location.pathname.toLowerCase();
        const activeSection = Object.entries(menuBySection).find(([_, items]) =>
            items.some((item) => currentPath.startsWith(item.href.toLowerCase()))
        );
        if (activeSection) {
            setActiveSection(activeSection[0]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const toggleSection = (section: string) => {
        setActiveSection((prev) => (prev === section ? null : section));
    };

    return (
        <div className="pm-shell">
            {menuOpen && isMobile && !isExamView && (
                <div className="pm-sidebar-overlay" onClick={() => setMenuOpenAndSave(false)} />
            )}
            <div className="pm-top-bar">
                <div className={`pm-logo-box ${menuOpen ? 'is-open' : 'is-closed'}`}>
                    {user?.role === 'Super Admin' || user?.role === 'Student' ? (
                        <Link to="/user/dashboard">
                            <img src={logo} alt="Paradigm Logo" />
                        </Link>
                    ) : (
                        <a
                            href={
                                window.location.hostname === 'localhost'
                                    ? 'http://localhost:5174'
                                    : 'https://teacher.paradigmmath.com'
                            }
                        >
                            <img src={logo} alt="Paradigm Logo" />
                        </a>
                    )}
                </div>

                <header className="pm-header" style={{ boxShadow: `0 0 20px var(--pm-g${grade})` }}>
                    <div className="pm-header__left">
                        {isExamView ? null : (
                            <SimpleButton
                                onClick={() => setMenuOpenAndSave(!menuOpen)}
                                className="pm-header__toggle"
                                sx={gradeColorButtonSx}
                            >
                                <FaBars size={20} />
                            </SimpleButton>
                        )}
                    </div>

                    <div className="pm-header__right">
                        {isImpersonating ? (
                            <>
                                <span className="pm-header__welcome">
                                    Impersonating: {user?.name}
                                </span>
                                <SimpleButton
                                    onClick={handleStopImpersonation}
                                    sx={gradeColorButtonSx}
                                >
                                    End
                                </SimpleButton>
                            </>
                        ) : (
                            <span className="pm-header__welcome">Hello, {user?.name}</span>
                        )}

                        {!isExamView && (
                            <Tooltip title="Profile" arrow>
                                <Link to="/user/userProfile" className="pm-icon-link">
                                    <UserIcon
                                        style={{ width: '26px', height: '26px' }}
                                        color="var(--pm-secondary-light)"
                                    />
                                </Link>
                            </Tooltip>
                        )}

                        <Tooltip title="Log Out" arrow>
                            <button className="pm-icon-link" onClick={() => logout(true)}>
                                <LogoutIcon
                                    color="#FFF"
                                    style={{ width: '26px', height: '26px' }}
                                />
                            </button>
                        </Tooltip>
                    </div>
                </header>
            </div>

            <div className="pm-body">
                {!isExamView && (
                    <aside className={`pm-sidebar ${menuOpen ? 'is-open' : 'is-closed'}`}>
                        <nav className="pm-menu">
                            {Object.entries(menuBySection).map(([section, items]) => {
                                const isStandalone =
                                    items.length === 1 && items[0].name === section;
                                const isExpanded = activeSection === section;
                                const isPractice = section === 'Practice Pathway';

                                // Don't show Practice section if user doesn't have practice enabled
                                if (isPractice && !user?.isPracticeAllowed) {
                                    return null;
                                }

                                if (isStandalone) {
                                    const standaloneItem = items[0];
                                    return (
                                        <Link
                                            key={section}
                                            to={standaloneItem.href}
                                            className={`pm-menu__section-header pm-menu__standalone-link ${
                                                isActive(standaloneItem.href)
                                                    ? 'is-active-root'
                                                    : ''
                                            }`}
                                            style={{
                                                color: `var(--pm-g${grade}-light)`,
                                                textDecoration: 'none',
                                            }}
                                            target={
                                                standaloneItem.extra?.includes('external')
                                                    ? '_blank'
                                                    : undefined
                                            }
                                        >
                                            <span>{section}</span>
                                        </Link>
                                    );
                                }

                                return (
                                    <React.Fragment key={section}>
                                        <button
                                            className="pm-menu__section-header"
                                            onClick={() => toggleSection(section)}
                                            style={{ color: `var(--pm-g${grade}-light)` }}
                                        >
                                            <span>{section}</span>
                                            {isExpanded ? (
                                                <FaChevronDown size={12} />
                                            ) : (
                                                <FaChevronRight size={12} />
                                            )}
                                        </button>
                                        <MenuSectionList
                                            items={items}
                                            isExpanded={isExpanded}
                                            onItemClick={() =>
                                                isMobile && setMenuOpenAndSave(false)
                                            }
                                            isActive={isActive}
                                            renderNotification={renderNotification}
                                        />
                                    </React.Fragment>
                                );
                            })}
                        </nav>
                    </aside>
                )}
                <main className="pm-main-container" style={{ backgroundColor: containerBg }}>
                    <div className="pm-main-content">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Menus;

const MenuSectionList: React.FC<{
    items: MenuItem[];
    isExpanded: boolean;
    onItemClick: () => void;
    isActive: (href: string) => boolean;
    renderNotification: (name: string) => React.ReactNode;
}> = ({ items, isExpanded, onItemClick, isActive, renderNotification }) => {
    const contentRef = React.useRef<HTMLUListElement>(null);
    const [height, setHeight] = React.useState<number>(0);

    useEffect(() => {
        if (contentRef.current) {
            setHeight(isExpanded ? contentRef.current.scrollHeight : 0);
        }
    }, [isExpanded, items]);

    return (
        <ul
            ref={contentRef}
            className={`pm-menu__list ${isExpanded ? 'is-expanded' : 'is-collapsed'}`}
            style={{ height: `${height}px` }}
        >
            {items.map((item) => (
                <li key={item.href}>
                    <Link
                        to={item.href}
                        onClick={onItemClick}
                        className={`pm-menu__item ${isActive(item.href) ? 'is-active' : ''}`}
                        target={item.extra?.includes('external') ? '_blank' : undefined}
                    >
                        <span className="pm-menu__dot" />
                        {item.name}
                        {renderNotification(item.name)}
                    </Link>
                </li>
            ))}
        </ul>
    );
};
